import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { api } from "@repo/api-client";
import type { TeacherProfile } from "@repo/shared-types";

export default function TeachersPage() {
  const [teachers, setTeachers] = useState<TeacherProfile[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.listTeachers().then(({ teachers: t }) => {
      setTeachers(t);
      setLoading(false);
    });
  }, []);

  return (
    <div className="cap-page">
      <section className="cap-hero">
        <div className="container">
          <div className="cap-hero__shell cap-hero__shell--split">
            <div>
              <span className="cap-hero__badge">Our Teachers</span>
              <h1 className="display-1 cap-hero__title">Wisdom guides from many lineages</h1>
              <p className="cap-hero__subtitle">
                Meditation masters, dream scientists, Ayurvedic doctors, and devotional teachers, each
                bringing a distinct voice to the path.
              </p>
              <div className="hero-actions">
                <Link to="/courses" className="btn btn--gold">Explore courses</Link>
                <Link to="/library" className="btn btn--outline">Visit the library</Link>
              </div>
            </div>
            <aside className="cap-hero__aside">
              <div className="cap-hero__card">
                <div className="cap-stat-grid">
                  <div className="cap-stat"><span className="cap-stat__value">{teachers.length || 3}</span><span className="cap-stat__label">teachers</span></div>
                  <div className="cap-stat"><span className="cap-stat__value">4</span><span className="cap-stat__label">specialties</span></div>
                  <div className="cap-stat"><span className="cap-stat__value">9</span><span className="cap-stat__label">course pathways</span></div>
                  <div className="cap-stat"><span className="cap-stat__value">∞</span><span className="cap-stat__label">insights</span></div>
                </div>
              </div>
            </aside>
          </div>
        </div>
      </section>

      <section className="section section--surface-alt">
        <div className="container">
          {loading ? (
            <p className="text-muted body-lg text-center" style={{ padding: "4rem 0" }}>Loading teachers…</p>
          ) : teachers.length === 0 ? (
            <p className="text-muted body-lg text-center" style={{ padding: "4rem 0" }}>No teachers available.</p>
          ) : (
            <div className="cap-grid cap-grid--teachers">
              {teachers.map((teacher) => <TeacherCard key={teacher.id} teacher={teacher} />)}
            </div>
          )}
        </div>
      </section>
    </div>
  );
}

function TeacherCard({ teacher }: { teacher: TeacherProfile }) {
  return (
    <Link
      to={`/teachers/${teacher.slug}`}
      className="cap-card"
    >
      <div className="cap-card__media" style={{ minHeight: "15rem" }}>
        {teacher.photo_url ? <img src={teacher.photo_url} alt={teacher.name} /> : <span aria-hidden="true">🙏</span>}
      </div>
      <div className="cap-card__body">
        {teacher.title && <span className="cap-pill cap-pill--indigo">{teacher.title}</span>}
        <h3 className="heading-lg">{teacher.name}</h3>
        <p className="body-sm" style={{ color: "var(--c-ink-75)" }}>{teacher.short_bio}</p>
        <div className="cap-card__meta-row">
          {teacher.specialties.slice(0, 3).map((s) => (
            <span key={s} className="cap-pill" style={{ background: "rgba(61, 43, 31, 0.06)", color: "var(--c-muted)" }}>{s}</span>
          ))}
          {teacher.course_ids.length > 0 && (
            <span className="cap-pill" style={{ marginLeft: "auto", background: "rgba(245, 158, 11, 0.12)", color: "#b45309" }}>{teacher.course_ids.length} courses</span>
          )}
        </div>
      </div>
    </Link>
  );
}
